import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { exporterXlsx } from './exportXlsx';
import { lire, rapportDe, nomPropre, texte, nombre, entetes } from './tableauVersExcel';
import { DEFINITIONS } from '../definitions';
import './MenuBloc.css';

// ─── Menu du clic droit sur un bloc de rapport (2026-10-21) ──────────────────
// Remplace le bouton « Excel » au survol : l'interface reste nue, les actions
// sont au clic droit sur un bloc (carte de rapport).
//   · Exporter en Excel  — droit « Export Excel », tableau tel qu'affiché ;
//   · Copier en image    — même droit (c'est aussi une sortie de données) ;
//   · Afficher en grand  — pour tous : le bloc seul, en plein écran ;
//   · D'où viennent ces chiffres ? — pour tous, sur un bloc marqué
//     data-definitions : formules, sources, règles, et le calcul refait avec
//     les chiffres de la ligne cliquée (cf. definitions.js).
// Les deux premières demandent d'abord l'autorisation au serveur, qui
// journalise (POST /bi/exports). Maj + clic droit garde le menu du navigateur.

const BLOC = '.vt-card, .cr-card, .tr-card, .ob-card';
const titreBloc = (bloc) => {
  const h = bloc.querySelector('h2, h3');
  return nomPropre((h && h.textContent) || 'Bloc');
};

async function autoriser(bloc, format, lignes) {
  await api.post('/bi/exports', { rapport: nomPropre(rapportDe()), tableau: titreBloc(bloc), lignes, format });
}

export default function MenuBloc({ exportExcel }) {
  const [menu, setMenu] = useState(null); // { bloc, ligne, x, y }
  const [fiche, setFiche] = useState(null); // { def, nomLigne, calculs }
  const [message, setMessage] = useState('');
  const ref = useRef(null);
  const dire = (m) => { setMessage(m); setTimeout(() => setMessage(''), 3500); };

  useEffect(() => {
    const ouvrir = (e) => {
      if (e.shiftKey) return; // menu du navigateur
      const bloc = e.target.closest && e.target.closest(`main ${BLOC.split(', ').join(', main ')}`);
      if (!bloc) return;
      e.preventDefault();
      const w = 230, h = 140;
      const tr = e.target.closest('tbody tr, tfoot tr');
      setMenu({ bloc, ligne: tr && bloc.contains(tr) ? tr : null, x: Math.min(e.clientX, window.innerWidth - w - 8), y: Math.min(e.clientY, window.innerHeight - h - 8) });
    };
    const fermer = (e) => { if (!ref.current || !ref.current.contains(e.target)) setMenu(null); };
    const echap = (e) => { if (e.key === 'Escape') { setMenu(null); setFiche(null); } };
    document.addEventListener('contextmenu', ouvrir);
    document.addEventListener('mousedown', fermer);
    document.addEventListener('keydown', echap);
    const defile = () => setMenu(null);
    window.addEventListener('scroll', defile, { capture: true, passive: true });
    return () => {
      window.removeEventListener('scroll', defile, { capture: true });
      document.removeEventListener('contextmenu', ouvrir);
      document.removeEventListener('mousedown', fermer);
      document.removeEventListener('keydown', echap);
    };
  }, []);

  const table = menu && menu.bloc.querySelector('table');

  const excel = async () => {
    const bloc = menu.bloc; setMenu(null);
    const { colonnes, lignes } = lire(bloc.querySelector('table'));
    try { await autoriser(bloc, 'excel', lignes.length); } catch (e) { dire(e.response?.data?.error || 'Export impossible'); return; }
    const jour = new Date().toISOString().slice(0, 10);
    const t = titreBloc(bloc);
    exporterXlsx(`${nomPropre(rapportDe())} - ${t} - ${jour}.xlsx`, t.slice(0, 31) || 'Export', colonnes, lignes);
  };

  // Image du bloc entier (tableau ou graphique), sur le fond de la page.
  // Presse-papier si le navigateur l'accepte, sinon téléchargement en PNG.
  const image = async () => {
    const bloc = menu.bloc; setMenu(null);
    try { await autoriser(bloc, 'image', null); } catch (e) { dire(e.response?.data?.error || 'Copie impossible'); return; }
    const fond = getComputedStyle(document.body).backgroundColor;
    const { default: html2canvas } = await import('html2canvas'); // chargé à la demande
    const canvas = await html2canvas(bloc, { backgroundColor: fond, scale: Math.min(2, window.devicePixelRatio || 1), logging: false });
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      dire('Image copiée : collez-la dans un mail ou une présentation');
    } catch (e) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${nomPropre(rapportDe())} - ${titreBloc(bloc)}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      dire('Image téléchargée');
    }
  };

  // Fiche « D'où viennent ces chiffres ? ». Ligne retenue : celle du clic, à
  // défaut la ligne de total, à défaut la première.
  const origine = () => {
    const { bloc, ligne } = menu; setMenu(null);
    const def = DEFINITIONS[bloc.dataset.definitions];
    const table = bloc.querySelector('table');
    let calculs = []; let nomLigne = null;
    const tr = ligne || (table && (table.querySelector('tfoot tr') || table.querySelector('tbody tr')));
    if (table && tr) {
      const tetes = entetes(table) || [];
      const cellules = [...tr.children].map(texte);
      const val = (lib) => { const i = tetes.indexOf(lib); return i < 0 ? null : { t: cellules[i], n: nombre(cellules[i] || '') }; };
      nomLigne = (cellules[0] || '').split(' · ')[0] || null; // sans badge ni sous-ligne
      calculs = def.colonnes.filter((c) => c.calcul).map((c) => {
        const num = c.calcul.num.map(val); const den = c.calcul.den.map(val);
        if ([...num, ...den].some((x) => !x || x.n == null)) return { libelle: c.libelle, texte: null };
        const sn = num.reduce((a, x) => a + x.n, 0); const sd = den.reduce((a, x) => a + x.n, 0);
        const fmt = (l) => (l.length > 1 ? `(${l.map((x) => x.t).join(' + ')})` : l[0].t);
        const r = sd ? (sn / sd) * 100 : null;
        return { libelle: c.libelle, texte: `${fmt(num)} ÷ ${fmt(den)} = ${r == null ? '—' : `${r.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}` };
      });
    }
    setFiche({ def, nomLigne, calculs });
  };

  // Plein écran natif : Échap pour revenir.
  const grand = async () => {
    const bloc = menu.bloc; setMenu(null);
    try { await bloc.requestFullscreen(); } catch (e) { dire('Plein écran indisponible dans ce navigateur'); }
  };

  return (
    <>
      {menu && (
        <div className="mb-menu" ref={ref} role="menu" style={{ left: menu.x, top: menu.y }}>
          <div className="mb-titre">{titreBloc(menu.bloc)}</div>
          {exportExcel && table && <button type="button" role="menuitem" onClick={excel}>Exporter en Excel</button>}
          {exportExcel && <button type="button" role="menuitem" onClick={image}>Copier en image</button>}
          <button type="button" role="menuitem" onClick={grand}>Afficher en grand</button>
          {DEFINITIONS[menu.bloc.dataset.definitions] && <button type="button" role="menuitem" onClick={origine}>D'où viennent ces chiffres ?</button>}
        </div>
      )}
      {message && <div className="mb-msg" role="status">{message}</div>}
      {fiche && (
        <div className="mb-voile" onClick={() => setFiche(null)}>
          <div className="mb-fiche" role="dialog" aria-label="D'où viennent ces chiffres ?" onClick={(e) => e.stopPropagation()}>
            <div className="mb-fiche-h">
              <h2>D'où viennent ces chiffres ?</h2>
              <button type="button" className="mb-fermer" onClick={() => setFiche(null)} aria-label="Fermer">×</button>
            </div>
            <p className="mb-sous">{fiche.def.titre}</p>
            <h3>Les colonnes</h3>
            <dl className="mb-defs">
              {fiche.def.colonnes.map((c) => (
                <div key={c.libelle}>
                  <dt>{c.libelle}</dt>
                  <dd><b>{c.formule}</b>{c.detail && <span>{c.detail}</span>}</dd>
                </div>
              ))}
            </dl>
            {fiche.calculs.some((c) => c.texte) && (
              <>
                <h3>Le calcul{fiche.nomLigne ? ` pour « ${fiche.nomLigne} »` : ''}</h3>
                <ul className="mb-calculs">
                  {fiche.calculs.filter((c) => c.texte).map((c) => <li key={c.libelle}><b>{c.libelle}</b> = {c.texte}</li>)}
                </ul>
              </>
            )}
            <h3>À savoir pour lire ces chiffres</h3>
            <ul className="mb-regles">{fiche.def.regles.map((r) => <li key={r}>{r}</li>)}</ul>
            <h3>Sources</h3>
            <ul className="mb-regles">{fiche.def.sources.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
        </div>
      )}
    </>
  );
}
